import { distMi } from "./followedFires";

const titleCase = (s) =>
  s.replace(/_/g, " ").replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

/** Cluster prefix from a zone title — EMs prefix zones with the fire name,
 *  e.g. "Level 2 — LG Entiat - Level 2 (Get Set)" → "lg entiat". */
export function zoneClusterPrefix(title) {
  let t = String(title || "");
  t = t.replace(/^level\s*\d\s*[—:-]?\s*/i, "");
  t = t.split(/\s*[-–:]?\s*level\s*\d/i)[0];
  t = t.split("—")[0].trim().toLowerCase();
  return t.length >= 3 ? t : "";
}

const GENERIC_ZONE_WORDS = new Set([
  "creek", "lake", "river", "canyon", "mountain", "road", "ridge", "valley", "new", "fire", "smoke",
]);

/** Does a zone title reference this fire — by full name or by initials
 *  ("LG Entiat" → Little Giant)? Generic one-word names never match. */
export function fireNameInTitle(fireName, title) {
  const fname = String(fireName || "").trim().toLowerCase();
  if (!fname || GENERIC_ZONE_WORDS.has(fname)) return false;
  const t = String(title || "").toLowerCase();
  if (fname.length >= 5 && t.includes(fname)) return true;
  const ini = fname.split(/\s+/).map((w) => w[0]).join("");
  if (ini.length >= 2) {
    const first = zoneClusterPrefix(title).split(/\s+/)[0] || "";
    if (first === ini) return true;
  }
  return false;
}

/** Evac zones belonging to a fire, share-caption shape, worst level first.
 *  A zone matches by distance (zone radius + 10 mi), by the fire's name in
 *  its title, or by sharing a title prefix with an already-matched zone.
 *  STRICT per-fire rule: zones whose title names a DIFFERENT fire, or that
 *  are tagged exclusively to other fires, are excluded — overlapping
 *  perimeters (e.g. Modrite/Hudspeth/Bradeen Hill) multi-tag zones, and a
 *  fire's share must only carry its own evacuations. */
export function evacsForShare(alerts, lat, lon, fireName) {
  if (lat == null || lon == null || !alerts?.length) return [];
  const scored = alerts.map((a) => ({
    a,
    d: a.latitude != null ? distMi(lat, lon, a.latitude, a.longitude) : Infinity,
  }));
  const fname = String(fireName || "").trim().toLowerCase();
  // Does this zone belong to a different fire?
  const isOtherFire = (a) => {
    if (!fname) return false;
    if (fireNameInTitle(fireName, a.title)) return false;
    const tags = a.fire_names || [];
    if (tags.includes(fname)) {
      // Multi-tagged (overlapping perimeters): exclude only when the title
      // explicitly names one of the OTHER fires.
      return tags.some((n) => n !== fname && fireNameInTitle(n, a.title));
    }
    // Tagged exclusively to other fire(s) → not ours.
    return tags.length > 0;
  };
  const matched = scored.filter(
    ({ a, d }) =>
      !isOtherFire(a) &&
      (d <= (Number(a.radius_mi) || 1) + 10 ||
        (fname && (a.fire_names || []).includes(fname)) ||
        (d <= 60 && fireNameInTitle(fireName, a.title)))
  );
  const prefixes = new Set(
    matched.map(({ a }) => zoneClusterPrefix(a.title)).filter(Boolean)
  );
  const ids = new Set(matched.map(({ a }) => a.id));
  for (const s of scored) {
    if (ids.has(s.a.id) || s.d > 60 || isOtherFire(s.a)) continue;
    const p = zoneClusterPrefix(s.a.title);
    if (p && prefixes.has(p)) {
      matched.push(s);
      ids.add(s.a.id);
    }
  }
  return matched
    .sort((x, y) => (y.a.level || 0) - (x.a.level || 0) || x.d - y.d)
    .map(({ a, d }) => ({
      id: a.id,
      level: a.level,
      status: a.status || "active",
      title: a.title,
      description: a.description,
      radius_mi: a.radius_mi,
      created_by: a.created_by,
      created_at: a.created_at,
      updated_at: a.updated_at,
      is_perimeter: !!a.geometry,
      dist_mi: Math.round(d * 10) / 10,
    }));
}

const fmtWhen = (iso) =>
  new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const EVAC_WORD = { 1: "GET READY", 2: "GET SET", 3: "GO NOW" };

const STATUS_WORD = {
  active: "Currently Active",
  reduced: "Reduced",
  rescinded: "Rescinded",
  not_confirmed: "Status Not Confirmed",
};

// Most recent activity on a zone (updated_at or created_at), as epoch ms.
const zoneTs = (e) => {
  const u = e.updated_at ? new Date(e.updated_at).getTime() : 0;
  const c = e.created_at ? new Date(e.created_at).getTime() : 0;
  return Math.max(u, c);
};

// Dispatch-fed fires often lack structured acres/containment while the AI
// summary text carries them — parse the summary so the header never says
// "pending" when the numbers are right below it.
const parseAcresFrom = (text) => {
  const m = /([\d,]+(?:\.\d+)?)\s*acres?\b/i.exec(text || "");
  if (!m) return null;
  const n = parseFloat(m[1].replace(/,/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
};

const parseContainedFrom = (text) => {
  const t = text || "";
  const m =
    /(?<![\d.])(\d{1,3}(?:\.\d+)?)\s*(?:%|percent)\s*contain/i.exec(t) ||
    /contain\w*[^.\d%]{0,12}(?<![\d.])(\d{1,3}(?:\.\d+)?)\s*(?:%|percent)/i.exec(t);
  if (!m) return null;
  const n = Math.round(parseFloat(m[1]));
  return n >= 0 && n <= 100 ? n : null;
};

export function buildIncidentCaption({ name, county, counties, acres, contained, statusLabel, evacLevel, evacs, updatedAt, incNum, resources, url, lastSharedAt, summary, onlyNewEvacs = false }) {
  if (acres == null) acres = parseAcresFrom(summary);
  if (contained == null) contained = parseContainedFrom(summary);
  const countyStr = counties?.length ? counties.join("/") : county ? titleCase(county) : null;
  const DIV = "-".repeat(40);
  const lines = [];
  lines.push(`🔥 ${(name || "Fire").toUpperCase()}${countyStr ? ` — ${countyStr} Co.` : ""}${incNum ? ` · INC #${incNum}` : ""}`);
  // When the newest evacuation change was posted.
  if (evacs?.length) {
    const latest = Math.max(...evacs.map(zoneTs));
    if (latest > 0) lines.push(`🆕 NEW EVACUATIONS UPDATE: Latest Posted ${fmtWhen(latest)}`);
  }
  const size =
    acres != null
      ? `${acres >= 10 ? Math.round(acres).toLocaleString() : Math.round(acres * 10) / 10} acres`
      : "size pending";
  const st = String(statusLabel || "").toUpperCase();
  let cont;
  if (contained != null) cont = `${contained}% contained`;
  else if (st.includes("OUT")) cont = "✅ FIRE OUT";
  else if (st.includes("CONTROL")) cont = "✅ CONTROLLED";
  else if (st.includes("CONTAIN")) cont = "✅ CONTAINED";
  else cont = "containment pending";
  lines.push(`📏 ${size} · ${cont}`);
  if (resources?.length) {
    const shown = resources.slice(0, 10).join(", ");
    const more = resources.length > 10 ? ` +${resources.length - 10} more` : "";
    lines.push(`🚒 ${resources.length} unit${resources.length === 1 ? "" : "s"} assigned: ${shown}${more}`);
  }
  const maxLevel = evacs?.length ? Math.max(...evacs.map((e) => e.level || 0)) : evacLevel;
  if (maxLevel === 3) lines.push("⚠️ Level 3 GO NOW evacuations in effect");
  else if (maxLevel === 2) lines.push("⚠️ Level 2 GET SET evacuations in effect");
  else if (maxLevel === 1) lines.push("⚠️ Level 1 GET READY evacuations in effect");
  if (updatedAt) lines.push(`🕐 Updated ${fmtWhen(updatedAt)}`);
  if (summary) {
    lines.push(DIV);
    lines.push("📝 SUMMARY");
    lines.push(String(summary).trim());
  }
  if (evacs?.length) {
    // Tag a zone as 🆕 NEW (2) if its `created_at` is fresh, or 🔄 UPDATED
    // (1) if only its `updated_at` is fresh. "Fresh" means AFTER whichever
    // of these is EARLIER:
    //   • the last time this fire was shared (`lastSharedAt`), or
    //   • a rolling 24-hour window before now.
    // Using the earlier boundary means tags still show for zones that
    // changed today even if you're re-sharing an hour after your last blast.
    const rollingRef = Date.now() - 24 * 36e5;
    const shareRef = lastSharedAt ? new Date(lastSharedAt).getTime() : rollingRef;
    const ref = new Date(Math.min(rollingRef, shareRef));
    const isNew = (e) => {
      if (e.created_at && new Date(e.created_at) > ref) return 2;
      if (e.updated_at && new Date(e.updated_at) > ref) return 1;
      return 0;
    };
    const TAG = { 2: " · 🆕 NEW", 1: " · 🔄 UPDATED" };
    // Zones listed oldest → newest so the post reads chronologically and
    // the latest change sits at the bottom, matching the "Latest" top line.
    let ordered = [...evacs].sort((a, b) => zoneTs(a) - zoneTs(b));
    if (onlyNewEvacs) {
      // Carry over only zones that are NEW or UPDATED since the last share
      // (or 24h) — long-running zone lists stay out of repeat posts.
      const freshOnly = ordered.filter((e) => isNew(e) > 0);
      if (freshOnly.length === 0) {
        lines.push(DIV);
        lines.push(
          `📋 ${evacs.length} evacuation zone${evacs.length === 1 ? "" : "s"} still active — no new changes since last share. Full list on the live map.`
        );
      }
      ordered = freshOnly;
    }
    ordered.forEach((e) => {
      lines.push(DIV);
      lines.push(
        `LEVEL ${e.level || "?"} · ${EVAC_WORD[e.level] || ""} — ${STATUS_WORD[e.status || "active"] || STATUS_WORD.active}${TAG[isNew(e)] || ""}`.trim()
      );
      if (e.title) lines.push(`• ${e.title}`);
      if (e.description) lines.push(`• ${e.description.slice(0, 400)}`);
      const meta = [];
      if (e.radius_mi) meta.push(`~${e.radius_mi} mi radius`);
      const by = e.created_by || "County EM";
      meta.push(`posted by ${by}`);
      if (e.is_perimeter && !/perimeter/i.test(by)) meta.push("Perimeter");
      lines.push(`• ${meta.join(" · ")}`);
      const when = [];
      if (e.created_at) when.push(`Posted ${fmtWhen(e.created_at)}`);
      if (e.updated_at && e.updated_at !== e.created_at) when.push(`Updated ${fmtWhen(e.updated_at)}`);
      if (when.length) lines.push(`• ${when.join(" · ")}`);
    });
    lines.push(DIV);
  }
  lines.push("", "Live map & details:", url);
  return lines.join("\n");
}

export function buildSnapshotCaption({ regionLabel, active, url }) {
  const d = new Date().toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const lines = [`🗓️ ${(regionLabel || "").toUpperCase()} FIRE SNAPSHOT — ${d}`];
  if (active != null) lines.push(`🔥 ${active} active incident${active === 1 ? "" : "s"}`);
  lines.push("", "Today's fire summary card:", url);
  return lines.join("\n");
}
